import React from 'react';
import { useNavigate } from '@tanstack/react-router';

interface HouseDetailMenuProps {
  id: string;
  houseName: string;
  onClose: () => void;
  onDelete: () => void;
}

export const HouseDetailMenu: React.FC<HouseDetailMenuProps> = ({
  id,
  onClose,
  onDelete,
}) => {
  const navigate = useNavigate();

  // --- Define all handlers
  const handleViewResidents = () => {
    navigate({ to: '/residents', search: { houseId: id } });
    onClose();
  };

  const handleEditHouse = () => {
    navigate({ to: `/houses/edit/$houseId`, params: { houseId: id } });
    onClose();
  };

  const handleAssignManagers = () => {
    // TODO: Open assign managers modal or navigate to managers page
    console.log(`Assigning managers to house ${id}`);
    onClose();
  };

  const handleViewFinance = () => {
    // TODO: Navigate to financial page filtered by house
    console.log(`Viewing financial overview for house ${id}`);
    onClose();
  };

  const handleDeleteHouse = () => {
    onDelete();
    onClose();
  };

  // --- Define menu items dynamically ---
  const menuItems = [
    {
      label: 'View Residents',
      onClick: handleViewResidents,
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.99961 8.0249C6.92461 8.0249 6.01003 7.64782 5.25586 6.89365C4.50169 6.13949 4.12461 5.2249 4.12461 4.1499C4.12461 3.0749 4.50169 2.16032 5.25586 1.40615C6.01003 0.651986 6.92461 0.274902 7.99961 0.274902C9.07461 0.274902 9.98919 0.651986 10.7434 1.40615C11.4975 2.16032 11.8746 3.0749 11.8746 4.1499C11.8746 5.2249 11.4975 6.13949 10.7434 6.89365C9.98919 7.64782 9.07461 8.0249 7.99961 8.0249ZM0.224609 15.6749V13.0249C0.224609 12.4749 0.364193 11.9728 0.643359 11.5187C0.922526 11.0645 1.29978 10.7165 1.77511 10.4747C2.79144 9.97482 3.81836 9.59782 4.85586 9.34365C5.89336 9.08949 6.94128 8.9624 7.99961 8.9624C9.05794 8.9624 10.1059 9.08949 11.1434 9.34365C12.1809 9.59782 13.2078 9.97482 14.2241 10.4747C14.6994 10.7165 15.0767 11.0645 15.3559 11.5187C15.635 11.9728 15.7746 12.4749 15.7746 13.0249V15.6749H0.224609Z"
            fill="#474747"
          />
        </svg>
      ),
    },
    {
      label: 'Edit House',
      onClick: handleEditHouse,
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 16H3.425L13.2 6.225L11.775 4.8L2 14.575V16ZM0 18V13.75L13.2 0.575C13.4 0.391667 13.6208 0.25 13.8625 0.15C14.1042 0.05 14.3583 0 14.625 0C14.8917 0 15.15 0.05 15.4 0.15C15.65 0.25 15.8667 0.4 16.05 0.6L17.425 2C17.625 2.18333 17.7708 2.4 17.8625 2.65C17.9542 2.9 18 3.15 18 3.4C18 3.66667 17.9542 3.92083 17.8625 4.1625C17.7708 4.40417 17.625 4.625 17.425 4.825L4.25 18H0Z"
            fill="#474747"
          />
        </svg>
      ),
    },
    {
      label: 'Assign House Managers',
      onClick: handleAssignManagers,
      icon: (
        <svg
          width="18"
          height="20"
          viewBox="0 0 18 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.09961 19.775C1.58394 19.775 1.14253 19.5914 0.775359 19.2242C0.408193 18.8571 0.224609 18.4157 0.224609 17.9V4.1C0.224609 3.58433 0.408193 3.14292 0.775359 2.77575C1.14253 2.40858 1.58394 2.225 2.09961 2.225H6.34961C6.54128 1.63333 6.87794 1.15625 7.35961 0.79375C7.84111 0.43125 8.38694 0.25 8.99711 0.25C9.60711 0.25 10.1538 0.43125 10.6371 0.79375C11.1204 1.15625 11.4579 1.63333 11.6496 2.225H15.8996C16.4153 2.225 16.8567 2.40858 17.2239 2.77575C17.591 3.14292 17.7746 3.58433 17.7746 4.1V17.9C17.7746 18.4157 17.591 18.8571 17.2239 19.2242C16.8567 19.5914 16.4153 19.775 15.8996 19.775H2.09961Z"
            fill="#474747"
          />
        </svg>
      ),
    },
    {
      label: 'View Financial Overview',
      onClick: handleViewFinance,
      icon: (
        <svg
          width="20"
          height="18"
          viewBox="0 0 20 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.25 17.7751V15.9001H19.75V17.7751H0.25ZM1.225 14.9001V8.2251H4.1V14.9001H1.225ZM6.1125 14.9001V3.2251H8.9875V14.9001H6.1125ZM11 14.9001V6.2251H13.875V14.9001H11ZM15.9 14.9001V0.225098H18.775V14.9001H15.9Z"
            fill="#474747"
          />
        </svg>
      ),
    },
    {
      label: 'Delete House',
      onClick: handleDeleteHouse,
      icon: (
        <svg
          width="16"
          height="18"
          viewBox="0 0 16 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
            fill="#E03A3D"
          />
        </svg>
      ),
      danger: true,
    },
  ];

  // --- Render dynamically ---
  return (
    <div className="relative inline-block text-left">
      <div className="absolute right-0 mt-2 w-72 origin-top-right bg-white border border-gray-200 shadow-lg focus:outline-none z-50 rounded-lg">
        <div className="py-1">
          {menuItems.map(({ label, icon, onClick, danger }) => (
            <div
              key={label}
              onClick={onClick}
              className={`w-full text-left p-4 flex items-center gap-4 transition-colors hover:bg-gray-100 cursor-pointer ${
                danger ? 'text-red-600' : 'text-text-primary'
              }`}
            >
              {icon}
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
